import * as Sentry from '@sentry/react';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { TENANT_KEY, TOKEN_KEY } from './tenant';

/**
 * Requests whose own 401 is a normal answer rather than an expired session. A wrong
 * password must render in the sign-in form, not bounce the page back to itself.
 */
const AUTH_EXEMPT_PATHS = ['/auth/login'];

/**
 * Carries no role restriction, so a 401 from it is always about the session itself.
 */
const SESSION_CHECK_PATH = '/auth/verify-token';

/**
 * Khufu answers a role denial with 401 and the same code, message and details it uses
 * for a bad token, so status alone cannot tell "your session ended" from "you may not
 * open this". These two are the only 401s that are unambiguously about the session.
 */
const TENANT_MISMATCH_DETAILS = 'Token was not issued for this company';
const MISSING_TOKEN_MESSAGE = 'Not authorized to access this route';

/**
 * Deliberately conservative: anything it cannot positively identify as a dead session
 * falls through to the ordinary error notice and the user stays signed in.
 *
 * Treating every 401 as an expiry signed non-admins out on sight, because each
 * /dashboard/* route is restricted to admins and the dashboard is what "/" renders —
 * so a supervisor logging in was bounced straight back to the login screen.
 */
export const isSessionExpiry = (error: AxiosError<{ error?: { message?: string; details?: string } }>): boolean => {
  if (error.response?.status !== 401) {
    return false;
  }

  const url = error.config?.url ?? '';

  if (AUTH_EXEMPT_PATHS.some(path => url.startsWith(path)) || window.location.pathname === '/login') {
    return false;
  }

  if (url.startsWith(SESSION_CHECK_PATH)) {
    return true;
  }

  const body = error.response?.data?.error;

  return body?.details === TENANT_MISMATCH_DETAILS || body?.message === MISSING_TOKEN_MESSAGE;
};

/**
 * Khufu rejects any token whose tenant claim does not match the company being addressed,
 * and tokens minted before tenancy existed carry no claim at all. Everyone is signed out
 * once when that reaches production, so an unhandled 401 would otherwise leave an open
 * tab looping error toasts with no way back to the login screen.
 *
 * A full document load is deliberate: it drops the React Query cache and the rehydrated
 * Redux state together, which a client-side redirect would leave behind.
 */
const handleSessionExpiry = () => {
  localStorage.removeItem(TOKEN_KEY);
  window.location.replace('/login');
};

let axiosInstance: AxiosInstance | null = null;

export const getAxiosInstance = (): AxiosInstance => {
  if (!axiosInstance) {
    axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Key': TENANT_KEY
      }
    });

    axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (isSessionExpiry(error)) {
          handleSessionExpiry();
          return Promise.reject(error);
        }

        if (error.response) {
          console.error('Backend error: ', error);
          Sentry.captureException(error);
          return Promise.reject(error);
        } else if (error.request) {
          console.error('No response received: ', error);
          Sentry.captureException(error);
        } else {
          console.error('Error in request: ', error.message);
          Sentry.captureException(error);
        }
        return Promise.reject(error);
      }
    );
  }
  return axiosInstance;
};

/**
 * Authorization is attached per request rather than written onto the shared instance's
 * defaults. Writing it to the defaults left the token on every later call including
 * `/auth/login`, which under multi-tenancy would mean one company's sign-in carrying
 * another company's token.
 */
export const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem(TOKEN_KEY);

  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface AuthApiParams {
  method: AxiosRequestConfig['method'];
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
}

export const authApi = async <T>({ method, url, data, params }: AuthApiParams) => {
  const axiosInstance = getAxiosInstance();
  const response: AxiosResponse = await axiosInstance({
    method,
    url,
    data,
    params,
    headers: authHeaders()
  });
  return response.data as T;
};

const api = async (method: AxiosRequestConfig['method'], url: string, data?: unknown) => {
  const axiosInstance = getAxiosInstance();
  const response: AxiosResponse = await axiosInstance({
    method,
    url,
    data
  });
  return response.data;
};

export default api;
